import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Mini heatmap + bulleted list of upcoming occurrences for a recurrence rule.
 *
 * Props:
 *  - startDate: ISO YYYY-MM-DD (typically the task's due_date)
 *  - recurrence: structured recurrence rule object (or null)
 *  - recurring: legacy string ("daily" | "weekly" | …) — kept for backward compat
 *  - weeks: how many weeks of preview to draw (default 8)
 *  - accentColor: workspace accent
 */
export default function SchedulePreview({
  startDate,
  recurrence,
  recurring,
  weeks = 8,
  accentColor = "#0281FF",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Stable cache key — only refetch when these change
  const key = useMemo(
    () =>
      JSON.stringify({
        startDate,
        recurrence: recurrence || null,
        recurring: recurring || null,
        weeks,
      }),
    [startDate, recurrence, recurring, weeks]
  );

  useEffect(() => {
    if (!startDate) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/tasks/recurrence/preview", {
        start_date: startDate,
        recurrence: recurrence || null,
        recurring: recurring || null,
        weeks,
      })
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.response?.data?.detail || "Couldn't compute preview");
          setData(null);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a weeks×7 grid starting from the Sunday on/before startDate
  const grid = useMemo(() => {
    if (!startDate) return null;
    const start = new Date(`${startDate}T00:00:00`);
    const sundayBefore = new Date(start);
    sundayBefore.setDate(sundayBefore.getDate() - sundayBefore.getDay());
    const rows = [];
    for (let w = 0; w < weeks; w++) {
      const row = [];
      for (let d = 0; d < 7; d++) {
        const dt = new Date(sundayBefore);
        dt.setDate(sundayBefore.getDate() + w * 7 + d);
        const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
          dt.getDate()
        ).padStart(2, "0")}`;
        row.push({ iso, day: dt.getDate(), month: dt.getMonth() });
      }
      rows.push(row);
    }
    return rows;
  }, [startDate, weeks]);

  if (!startDate) return null;

  const occurrenceSet = new Set(data?.occurrences || []);

  return (
    <div
      data-testid="schedule-preview"
      className="rounded-lg border border-border bg-secondary/20 p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarClock
            size={12}
            strokeWidth={2.2}
            style={{ color: accentColor }}
          />
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-bold"
            style={{ color: accentColor }}
          >
            Schedule preview
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground font-mono">
          {loading ? (
            <Loader2 size={11} className="animate-spin" />
          ) : data?.count ? (
            `${data.count}× · ${data.summary}`
          ) : (
            ""
          )}
        </span>
      </div>

      {error ? (
        <div className="text-[11px] text-red-500" data-testid="schedule-preview-error">
          {error}
        </div>
      ) : (
        <>
          {/* Mini heatmap */}
          {grid && (
            <div data-testid="schedule-preview-grid" className="space-y-1">
              <div className="grid grid-cols-7 gap-0.5 text-[8px] uppercase tracking-wider text-muted-foreground font-mono">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                  <div key={i} className="text-center">
                    {d}
                  </div>
                ))}
              </div>
              {grid.map((row, ri) => (
                <div key={ri} className="grid grid-cols-7 gap-0.5">
                  {row.map((cell) => {
                    const isOccurrence = occurrenceSet.has(cell.iso);
                    const isStart = cell.iso === startDate;
                    return (
                      <div
                        key={cell.iso}
                        title={cell.iso}
                        data-testid={
                          isOccurrence ? `schedule-occurrence-${cell.iso}` : undefined
                        }
                        className="h-5 flex items-center justify-center text-[9px] font-mono rounded-sm transition-colors"
                        style={
                          isStart
                            ? {
                                backgroundColor: accentColor,
                                color: "#fff",
                                fontWeight: 700,
                                boxShadow: `0 0 0 1.5px ${accentColor}`,
                              }
                            : isOccurrence
                            ? {
                                backgroundColor: `${accentColor}33`,
                                color: accentColor,
                                fontWeight: 600,
                              }
                            : {
                                backgroundColor: "transparent",
                                color: "hsl(var(--muted-foreground))",
                                opacity: 0.5,
                              }
                        }
                      >
                        {cell.day}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Next occurrences list (collapsed to top 5) */}
          {data?.occurrences?.length > 0 && (
            <ul
              data-testid="schedule-preview-list"
              className="mt-2 space-y-0.5 text-[10px] text-muted-foreground"
            >
              {data.occurrences.slice(0, 5).map((iso) => {
                const d = new Date(`${iso}T00:00:00`);
                return (
                  <li key={iso} className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-1 h-1 rounded-full"
                      style={{ backgroundColor: accentColor }}
                    />
                    <span className="font-mono tabular-nums">
                      {d.toLocaleDateString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    {iso === startDate && (
                      <span
                        className="text-[9px] uppercase tracking-wider font-bold ml-1"
                        style={{ color: accentColor }}
                      >
                        Start
                      </span>
                    )}
                  </li>
                );
              })}
              {data.occurrences.length > 5 && (
                <li className="text-[10px] pl-2 opacity-70">
                  + {data.occurrences.length - 5} more in next {weeks} weeks
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
